/**
 * This file contains the modal composable. This composable is used to open a modal from anywhere in the application.
 * It uses an event bus to communicate with the modal component.
 * The modal component is a wrapper around TairoModal from the Tairo UI library. (https://tairo.cssninja.io/documentation/reference/tairo/modal)
 */

const DIALOG_OPEN_EVENT = 'DIALOG_OPEN_EVENT' // Define the event name for the dialog open event
const eventBus = new EventTarget()
import type { MODAL_COMPONENTS } from 'components/Modal/components'

import type { ComponentProperties } from './types'

export const ModalContext = Symbol.for('ModalContext')

type ModalKind = keyof typeof MODAL_COMPONENTS // Create a type for the modal kind. This is the key of the modal components object

// Create a type for the default props of the modal component
export type ModalDefaultProperties = {
  [key: string]: any
  actions?: {
    color?: string
    event: string
    flavor?: string
    label: string
  }[]
  onDialogClose?: () => void
  title: string
  wrapper?: {
    classes?: any
    footerAlign?: string
    size?: string
  }
}

// Create our modal context type. This uses the props from the modal component and adds some default props for the wrapper and actions
type ModalContext<TKind extends ModalKind> = Omit<
  ComponentProperties<(typeof MODAL_COMPONENTS)[TKind]>,
  'onDialogClose'
> &
  ModalDefaultProperties

export type ModalEventPayload<TKind extends ModalKind = ModalKind> = {
  context: ModalContext<TKind>
  kind: ModalKind
}

// Create a useModal composable that has the modal functionality
function open<TKind extends ModalKind>(
  kind: TKind,
  context: ModalContext<TKind>,
) {
  eventBus.dispatchEvent(
    new CustomEvent(DIALOG_OPEN_EVENT, { detail: { context, kind } }),
  )
}

function subscribe(handler: (payload: ModalEventPayload) => void) {
  function handleEvent(event: CustomEvent<ModalEventPayload>) {
    handler(event.detail)
  }
  eventBus.addEventListener(DIALOG_OPEN_EVENT, handleEvent as EventListener)

  return () => {
    eventBus.removeEventListener(
      DIALOG_OPEN_EVENT,
      handleEvent as EventListener,
    )
  }
}

export const useModals = () => {
  return {
    open,
    subscribe,
  }
}
